import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useKioskMode } from '../../contexts/KioskModeContext';
import restaurantService from '../../services/restaurantService';
import { kioskOrdersPath, readKioskQueueToken } from './kioskPaths';
import styles from './KioskConfirmation.module.css';

const COUNTDOWN_SECONDS = 30;

type LocationState = {
  token?: string;
  queueTokenLabel?: string;
  orderId?: number | string;
};

const KioskConfirmation: React.FC = () => {
  const { appName } = useParams<{ appName: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { kioskGuestLogout, role: kioskRole } = useKioskMode();
  const state = (location.state || {}) as LocationState;

  useEffect(() => {
    if (kioskRole !== 'TABLE_KIOSK' || !appName) {
      return;
    }
    const orderId = searchParams.get('orderId') ?? (state.orderId != null ? String(state.orderId) : null);
    const payment = searchParams.get('payment');
    const target = orderId
      ? `${kioskOrdersPath(appName, orderId)}${payment ? `?payment=${encodeURIComponent(payment)}` : ''}`
      : kioskOrdersPath(appName);
    navigate(target, { replace: true });
  }, [appName, kioskRole, navigate, searchParams, state.orderId]);

  const orderIdFromQuery = searchParams.get('orderId');
  const paymentStatus = searchParams.get('payment');
  const orderIdKey = orderIdFromQuery ?? (state.orderId != null ? String(state.orderId) : null);

  const initialToken = useMemo(() => {
    return (
      state.queueTokenLabel ||
      state.token ||
      searchParams.get('token') ||
      readKioskQueueToken(orderIdKey) ||
      null
    );
  }, [orderIdKey, searchParams, state.queueTokenLabel, state.token]);

  const [displayToken, setDisplayToken] = useState<string | null>(initialToken);
  const [secondsLeft, setSecondsLeft] = useState(COUNTDOWN_SECONDS);
  const finishedRef = React.useRef(false);

  const finish = React.useCallback(() => {
    if (finishedRef.current || !appName) {
      return;
    }
    finishedRef.current = true;
    kioskGuestLogout(appName, 'POST_ORDER');
  }, [appName, kioskGuestLogout]);

  useEffect(() => {
    setDisplayToken(initialToken);
  }, [initialToken]);

  useEffect(() => {
    if (displayToken || !appName || !orderIdKey) {
      return;
    }
    const orderId = Number(orderIdKey);
    if (!Number.isFinite(orderId)) {
      return;
    }
    let cancelled = false;
    let attempts = 0;
    const maxAttempts = paymentStatus === 'SUCCESS' ? 20 : 8;

    const loadToken = () => {
      if (cancelled) {
        return;
      }
      restaurantService
        .getOrderDetails(appName, orderId)
        .then((response) => {
          if (cancelled) {
            return;
          }
          const label = response.queueTokenLabel;
          if (label) {
            setDisplayToken(label);
            return;
          }
          attempts += 1;
          if (attempts < maxAttempts) {
            window.setTimeout(loadToken, 2000);
          }
        })
        .catch(() => {
          if (!cancelled && attempts < maxAttempts) {
            attempts += 1;
            window.setTimeout(loadToken, 2000);
          }
        });
    };

    loadToken();
    return () => {
      cancelled = true;
    };
  }, [appName, displayToken, orderIdKey, paymentStatus]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          window.clearInterval(timer);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (secondsLeft === 0) {
      finish();
    }
  }, [secondsLeft, finish]);

  useEffect(() => {
    if (!paymentStatus || finishedRef.current) {
      return;
    }
    try {
      (window as Window & { PepparKiosk?: { notifyPaymentActive?: (a: boolean) => void } }).PepparKiosk
        ?.notifyPaymentActive?.(false);
    } catch {
      /* optional native shell */
    }
  }, [paymentStatus]);

  const tokenText = displayToken || (orderIdKey ? 'Preparing your number…' : '—');

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <p className={styles.eyebrow}>Your order number</p>
        <div className={`${styles.token} ${styles.tokenAccent}`} aria-live="polite">
          {tokenText}
        </div>
        <p className={styles.copy}>Show this number at the counter or to staff when your food is ready.</p>
        <p className={styles.countdown}>Returning in {secondsLeft}s</p>
        <button type="button" className={styles.done} onClick={finish}>
          Done
        </button>
      </div>
    </div>
  );
};

export default KioskConfirmation;
